<script setup>
/* Nutzerverwaltung — Zugangsanfragen und Rollen.
 *
 * WAS HIER NICHT GEHT und warum: Die Liste aller Konten (auth.users) ist fuer
 * die App nicht erreichbar. Sie abzufragen braeuchte den Serverschluessel,
 * der niemals im Browser landen darf. Angezeigt wird deshalb die Tabelle
 * `profiles` — dort bekommt jedes Konto beim Anlegen automatisch eine Zeile
 * (Ausloeser in supabase/profiles.sql, Abschnitt 5).
 *
 * Folge: Wer vor dem Einspielen des Skripts angelegt wurde, taucht nur auf,
 * wenn Abschnitt 6 (Nachtragen) gelaufen ist. Das Skript erledigt das, wenn
 * es vollstaendig ausgefuehrt wird.
 *
 * Einladen kann die App ebenfalls nicht — das passiert im Supabase-Dashboard.
 * "Angenommen" heisst hier: vorgemerkt, Einladung folgt von Hand.
 */

import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import UserAvatar from './UserAvatar.vue';
import AccessRequestsPanel from './AccessRequestsPanel.vue';
import { useAuth } from '../composables/useAuth.js';
import { useProfile } from '../composables/useProfile.js';
import { useAccessRequests } from '../composables/useAccessRequests.js';
import { useConfirm } from '../composables/useConfirm.js';
import { useUnsavedChanges } from '../composables/useUnsavedChanges.js';
import { loadProfiles, setProfileRole } from '../supabase/profiles.js';
import { ROLES } from '../domain/permissions.js';
import { identityLines } from '../domain/identity.js';

const open = defineModel({ type: Boolean, default: false });

const { t } = useI18n();
const { user } = useAuth();
const { tableAvailable } = useProfile();
const { pendingCount } = useAccessRequests();
const { confirm } = useConfirm();
const { setDirty } = useUnsavedChanges();

const tab = ref('requests');
const users = ref([]);
const loading = ref(false);
const error = ref(null);
const busyId = ref(null);
const saving = ref(false);
const saved = ref(false);
const saveError = ref(null);

/* Letzter gespeicherte Stand je Zeile, für den Speichern-Knopf (TODO.md
   "Speichern-Knopf statt stillem Speichern" — gilt laut Auftrag auch für die
   Rollenänderung hier, nicht nur für Spielerdaten). Ein Klick auf den
   Rollen-Umschalter ändert `row.role` jetzt nur noch lokal, gespeichert wird
   erst über den Knopf. */
const originalRoles = ref(new Map());

const dirty = computed(() =>
  users.value.some((row) => row.role !== originalRoles.value.get(row.id))
);

watch(dirty, (isDirty) => {
  setDirty('user-admin-roles', isDirty);
  if (isDirty) {
    saved.value = false;
    saveError.value = null;
  }
});

const ready = computed(() => tableAvailable.value !== false && error.value !== 'profilesMissing');

function nameOf(row) {
  return row.full_name || row.email || t('users.unnamed');
}

/* Ohne Namen oder mit der Adresse als Namen steht die Adresse nur einmal (domain/identity.js). */
function linesOf(row) {
  const l = identityLines(row.full_name, row.email);
  return { title: l.title || t('users.unnamed'), subtitle: l.subtitle };
}

async function fetchUsers() {
  loading.value = true;
  error.value = null;
  try {
    users.value = await loadProfiles();
    originalRoles.value = new Map(users.value.map((row) => [row.id, row.role]));
    saved.value = false;
    saveError.value = null;
  } catch (e) {
    error.value = e.message || 'loadFailed';
    users.value = [];
  } finally {
    loading.value = false;
  }
}

/* Ändert die Rolle nur noch lokal (TODO.md "Speichern-Knopf statt stillem
   Speichern") — gespeichert wird erst über saveRoles(). Die Rückfrage vorm
   Vergeben von Adminrechten bleibt an der Auswahl selbst hängen, nicht am
   Speichern, damit man weiß, wofür man gerade zustimmt. */
async function changeRole(row, next) {
  if (next === row.role) return;

  if (next === 'admin') {
    const ok = await confirm({
      title: t('confirm.grantAdminTitle'),
      message: t('confirm.grantAdmin', { name: nameOf(row) }),
      confirmLabel: t('users.makeAdmin'),
    });
    if (!ok) return;
  }

  row.role = next;
}

/* Speichert alle Zeilen, deren Rolle sich vom zuletzt geladenen Stand
   unterscheidet. Schlägt eine einzelne Zeile fehl, bleibt nur sie auf dem
   neuen (unbestätigten) Wert stehen und zählt weiterhin als ungespeichert —
   der Rest wird nicht verworfen. */
async function saveRoles() {
  const changed = users.value.filter((row) => row.role !== originalRoles.value.get(row.id));
  if (!changed.length) return;

  saving.value = true;
  saveError.value = null;
  let failed = false;
  for (const row of changed) {
    busyId.value = row.id;
    try {
      await setProfileRole(row.id, row.role);
      originalRoles.value.set(row.id, row.role);
    } catch (e) {
      saveError.value = e.message || 'saveFailed';
      failed = true;
    }
  }
  busyId.value = null;
  saving.value = false;
  saved.value = !failed;
}

/* Schließen speichert nicht mehr automatisch. Gibt es ungespeicherte
   Rollenänderungen, fragt eine Rückfrage nach; bei Bestätigung werden die
   lokalen Änderungen auf den zuletzt gespeicherten Stand zurückgesetzt,
   nicht nur ausgeblendet — sonst stünde beim nächsten Öffnen (kein erneutes
   Laden, siehe watch unten) eine nie gespeicherte Rolle da, die aussieht wie
   die aktuelle. */
async function requestClose() {
  if (dirty.value) {
    const ok = await confirm({
      title: t('confirm.discardChangesTitle'),
      message: t('confirm.discardChanges'),
    });
    if (!ok) return;
    users.value.forEach((row) => {
      if (originalRoles.value.has(row.id)) row.role = originalRoles.value.get(row.id);
    });
  }
  open.value = false;
}

/* Erst laden, wenn der Reiter tatsaechlich gebraucht wird. */
watch(
  [open, tab],
  ([isOpen, which]) => {
    if (isOpen && which === 'roles' && !users.value.length) fetchUsers();
  },
  { immediate: true }
);
</script>

<template>
  <OverlayModal v-if="open" :title="$t('users.title')" :width="620" :wide-width="900" @close="requestClose">
    <div class="ua-tabs" role="tablist">
      <button
        class="ua-tab"
        :class="{ active: tab === 'requests' }"
        role="tab"
        :aria-selected="tab === 'requests'"
        @click="tab = 'requests'"
      >
        {{ $t('users.tabRequests') }}
        <span v-if="pendingCount" class="dd-badge">{{ pendingCount }}</span>
      </button>
      <button
        class="ua-tab"
        :class="{ active: tab === 'roles' }"
        role="tab"
        :aria-selected="tab === 'roles'"
        @click="tab = 'roles'"
      >
        {{ $t('users.tabRoles') }}
      </button>
    </div>

    <AccessRequestsPanel v-if="tab === 'requests'" />

    <template v-else>
      <div v-if="loading" class="ua-note">{{ $t('common.loading') }}</div>

      <div v-else-if="!ready" class="ua-setup">
        <p>{{ $t('users.setupRequired') }}</p>
        <p class="ua-setup-hint">{{ $t('users.setupHint') }}</p>
      </div>

      <div v-else-if="error" class="ua-note error">{{ $t(`errors.${error}`) }}</div>

      <div v-else-if="!users.length" class="ua-note">{{ $t('users.noUsers') }}</div>

      <template v-else>
        <div v-for="row in users" :key="row.id" class="ua-row">
          <UserAvatar :name="nameOf(row)" :path="row.avatar_path || ''" :size="34" />

          <div class="ua-ident">
            <div class="ua-name">
              {{ linesOf(row).title }}
              <span v-if="row.id === user?.id" class="ua-you">{{ $t('users.you') }}</span>
            </div>
            <div v-if="linesOf(row).subtitle" class="ua-mail">{{ linesOf(row).subtitle }}</div>
          </div>

          <select
            class="ua-role"
            :value="row.role"
            :disabled="saving || busyId === row.id || row.id === user?.id"
            :title="row.id === user?.id ? $t('users.cannotChangeOwn') : ''"
            @change="changeRole(row, $event.target.value)"
          >
            <option v-for="r in ROLES" :key="r" :value="r">{{ $t(`roles.${r}`) }}</option>
          </select>
        </div>

        <p class="ua-hint">{{ $t('users.rolesHint') }}</p>
      </template>
    </template>

    <template #actions>
      <span v-if="tab === 'roles' && saveError" class="ua-save-msg error">
        {{ $t(`errors.${saveError}`) }}
      </span>
      <span v-else-if="tab === 'roles' && saved" class="ua-save-msg ok">
        {{ $t('users.saved') }}
      </span>
      <button class="btn ghost small" :disabled="saving" @click="requestClose">
        {{ $t('common.close') }}
      </button>
      <button
        v-if="tab === 'roles'"
        class="btn small"
        :disabled="!dirty || saving"
        @click="saveRoles"
      >
        {{ saving ? $t('common.loading') : $t('common.save') }}
      </button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.ua-tabs {
  display: flex;
  gap: 4px;
  margin: -6px -6px 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-soft);
}
.ua-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  font-family: var(--sans);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.14s, color 0.14s;
}
.ua-tab:hover {
  color: var(--text);
  background: var(--card-hover);
}
.ua-tab.active {
  color: var(--green);
  background: var(--green-bg);
}
.dd-badge {
  min-width: 16px;
  padding: 1px 6px;
  border-radius: 10px;
  background: var(--green);
  color: rgb(var(--v-theme-on-primary));
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
}

.ua-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-soft);
}
.ua-row:last-of-type {
  border-bottom: none;
}
.ua-ident {
  flex: 1;
  min-width: 0;
}
.ua-name {
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-size: 0.85rem;
  color: var(--text);
}
.ua-you {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--green);
}
.ua-mail {
  font-size: 0.74rem;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ua-role {
  flex: none;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 7px;
  color: var(--text);
  padding: 7px 9px;
  font: inherit;
  font-size: 0.78rem;
}
.ua-role:focus {
  outline: none;
  border-color: var(--green);
}
.ua-role:disabled {
  opacity: 0.55;
  cursor: default;
}

.ua-note {
  padding: 20px 0;
  font-size: 0.8rem;
  color: var(--dim);
  text-align: center;
}
.ua-note.error {
  color: var(--red);
}
.ua-setup {
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
  font-size: 0.8rem;
  color: var(--amber);
  line-height: 1.65;
}
.ua-setup-hint {
  margin-top: 8px;
  color: var(--dim);
  font-size: 0.74rem;
}
.ua-hint {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--border-soft);
  font-size: 0.72rem;
  color: var(--dim);
  line-height: 1.6;
}
.ua-save-msg {
  flex: 1;
  min-width: 0;
  align-self: center;
  font-size: 0.76rem;
  line-height: 1.5;
}
.ua-save-msg.ok {
  color: var(--green);
}
.ua-save-msg.error {
  color: var(--red);
}
</style>
